export const toRecord = <TKey extends string | number | symbol, TItem, TEntry = TItem>(
  arr: readonly TItem[],
  getKey: (item: TItem) => TKey,
  getEntry: (item: TItem, existing: TEntry | undefined, index: number) => TEntry,
): Record<TKey, TEntry> => {
  const record: Record<TKey, TEntry> = {} as Record<TKey, TEntry>;
  for (let i = 0; i < arr.length; i++) {
    const element = arr[i] as TItem;
    const key = getKey(element);
    record[key] = getEntry(element, record[key], i);
  }
  return record;
};

// Similar to toRecord but allows multiple keys per item
// e.g. if one item (student) belongs to multiple keys (school classes)
export const toRecordMultiKeys = <TKey extends string | number | symbol, TItem, TEntry = TItem>(
  arr: readonly TItem[],
  getKeys: (item: TItem) => TKey[],
  getEntry: (item: TItem, existing: TEntry | undefined, index: number) => TEntry,
): Record<TKey, TEntry> => {
  const record: Record<TKey, TEntry> = {} as Record<TKey, TEntry>;
  for (let i = 0; i < arr.length; i++) {
    const element = arr[i] as TItem;
    const keys = [...new Set(getKeys(element))];
    for (const key of keys) {
      record[key] = getEntry(element, record[key], i);
    }
  }
  return record;
};

export const immutableSort = <T>(values: readonly T[], sorterFunction: (a: T, b: T) => number): T[] => {
  return [...values].sort(sorterFunction);
};

export const uniquePrimitives = <T>(values: readonly T[]): T[] => {
  return [...new Set(values)];
};

/**
 * Merges two ordered collections by key. Incoming items replace existing items
 * without changing the position where that key first appeared.
 */
export const mergeByKey = <T, TKey>(existing: readonly T[], incoming: readonly T[], getKey: (item: T) => TKey): T[] => {
  const itemByKey = new Map<TKey, T>();
  for (const item of [...existing, ...incoming]) itemByKey.set(getKey(item), item);
  return [...itemByKey.values()];
};

export const uniqSorted = (arr: readonly number[]): number[] => {
  return Array.from(uniquePrimitives(arr)).sort((a, b) => a - b);
};

export const isContiguousRange = (values: number[]): boolean =>
  values.length === 0 || Math.max(...values) - Math.min(...values) + 1 === new Set(values).size;

export function isNotNullish<T>(value: T | null | undefined): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

export interface ArrayDiffResult<T> {
  toAdd: T[];
  toRemove: T[];
}

export function arrayDiff<T>(existing: readonly T[], updated: readonly T[]): ArrayDiffResult<T> {
  const toAdd = updated.filter((item) => !existing.includes(item));
  const toRemove = existing.filter((item) => !updated.includes(item));
  return { toAdd, toRemove };
}

export const areArraysEqualUnordered = (a: readonly number[], b: readonly number[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((left, right) => left - right);
  const sortedB = [...b].sort((left, right) => left - right);
  return sortedA.every((val, index) => val === sortedB[index]);
};

export function cloneArray<T>(arr?: readonly T[] | null): T[] {
  return Array.isArray(arr) ? [...arr] : [];
}
